<template>
  <div>
    <div class="package-selector-container">
      <custom-tooltip :text="tooltipText" :x="tooltipX" :y="tooltipY" />
      <div class="package-selector-header">
        <h3 class="title">รายการ Package</h3>
      </div>
      <div class="package-selector">
        <div class="search-list">
          <div class="search-input-wrapper">
            <el-input
v-model="searchKeyword" prefix-icon="el-icon-search" class="query-input"
              placeholder="ค้นหาแพ็กเกจ" clearable></el-input>
          </div>
          <div v-if="!isLoading && filteredSelectablePackages.length > 0" class="list-action-bar">
            <button class="select-all-button" @click="handleSelectAllPackages">
              เลือกทั้งหมด
            </button>
          </div>
          <div class="list-body">
            <div v-if="filteredSelectablePackages.length === 0 && !isLoading" class="empty">
              ไม่มีรายการ
            </div>
            <div
v-for="pkg in filteredSelectablePackages" :key="pkg.id" class="package-option selectable"
              @click="handleSelectPackage(pkg)" @mouseenter="showTooltip($event, pkg.name)" @mouseleave="hideTooltip">
              <img :src="pkg.imageUrl" class="icon" />
              <div class="meta">
                <h4 class="name">{{ pkg.name }}</h4>
                <p v-if="pkg.provider" class="provider">{{ pkg.provider.name }}</p>
              </div>
            </div>
          </div>
          <loading-spinner v-if="isLoading" />
        </div>
        <div class="selected-list">
          <div class="header">
            <h4 class="count">เลือกแล้ว {{ selectedList.length }} แพ็กเกจ</h4>
            <button :disabled="selectedList.length === 0" class="remove-all-button" @click="handleRemoveAllPackages">
              ลบทั้งหมด
            </button>
          </div>
          <div class="cards-body">
            <div v-if="selectedList.length === 0" class="empty">ไม่มีรายการที่เลือก</div>
            <div
v-for="pkg in selectedList" :key="pkg.id" class="package-card"
              @mouseenter="showTooltip($event, pkg.name)" @mouseleave="hideTooltip">
              <img :src="pkg.imageUrl" class="card-icon" />
              <div class="card-meta">
                <h4 class="card-name">{{ pkg.name }}</h4>
                <p v-if="pkg.provider" class="card-provider">{{ pkg.provider.name }}</p>
              </div>
              <button class="card-remove-button" @click="handleRemovePackage(pkg)">
                <img src="/static/icons/close-red.svg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Delete Confirmation Alert -->
    <alert-dialog
:visible="confirm.open" :title="confirm.title" :message="confirm.message" confirm-text="ลบทั้งหมด"
      confirm-type="danger" @confirm="confirmRemoveAll" @cancel="closeConfirm" />
  </div>
</template>

<script>
import Vue from "vue";
import { Input } from "element-ui";
import { GroupUseCase } from "@/usecases/GroupUseCase";
import { toCamelCaseObject } from "@/lib/util";
import LoadingSpinner from "@/components/general/LoadingSpinner";
import CustomTooltip from "./CustomTooltip";
import AlertDialog from "./AlertDialog";

export default {
  name: "PackageSelector",
  components: {
    "el-input": Input,
    CustomTooltip,
    LoadingSpinner,
    AlertDialog
  },
  props: {
    initialPackages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchKeyword: "",
      debouncedKeyword: "",
      allPackages: [],
      isLoading: false,
      selectedIds: {},
      selectedList: [],
      tooltipText: "",
      tooltipX: 0,
      tooltipY: 0,
      confirm: {
        open: false,
        title: 'ลบทั้งหมด',
        message: 'ต้องการลบรายการที่เลือกทั้งหมดใช่หรือไม่?'
      },
      debounceTimer: null
    };
  },
  computed: {
    filteredSelectablePackages() {
      const searchTerm = this.debouncedKeyword.toLowerCase();
      const ids = this.selectedIds;

      if (!searchTerm) {
        return this.allPackages.filter(pkg => !ids[pkg.id]);
      }

      return this.allPackages.filter(pkg =>
        !ids[pkg.id] && pkg.searchName.includes(searchTerm)
      );
    }
  },
  watch: {
    initialPackages: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.allPackages.length > 0) {
          this.matchAndSetPackages(newVal);
        }
      }
    },
    allPackages: {
      handler() {
        if (this.initialPackages && this.initialPackages.length > 0 && this.selectedList.length === 0) {
          this.matchAndSetPackages(this.initialPackages);
        }
      }
    },
    selectedList: {
      immediate: true,
      handler(newVal) {
        this.$emit('count-change', newVal.length);
      }
    },
    searchKeyword(newVal) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debouncedKeyword = newVal;
      }, 200);
    }
  },
  created() {
    this.setupAllPackages();
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    async setupAllPackages() {
      this.isLoading = true;
      try {
        const packages = await GroupUseCase.getAllPackage();

        // Check if packages is an array and not empty
        if (!packages || !Array.isArray(packages)) {
          this.allPackages = [];
          return;
        }

        // Convert to camel case and add searchName for filtering
        const converted = toCamelCaseObject(packages);
        this.allPackages = converted.map(pkg => ({
          ...pkg,
          searchName: pkg.name.toLowerCase()
        }));
      } catch (error) {
        this.allPackages = [];
      } finally {
        this.isLoading = false;
      }
    },
    handleSelectPackage(pkg) {
      Vue.set(this.selectedIds, pkg.id, true);
      this.selectedList = [pkg, ...this.selectedList];
    },
    handleSelectAllPackages() {
      const packagesToAdd = this.filteredSelectablePackages;
      packagesToAdd.forEach(pkg => {
        Vue.set(this.selectedIds, pkg.id, true);
      });
      this.selectedList = [...packagesToAdd, ...this.selectedList];
    },
    handleRemovePackage(pkg) {
      Vue.delete(this.selectedIds, pkg.id);
      this.selectedList = this.selectedList.filter(item => item.id !== pkg.id);
    },
    handleRemoveAllPackages() {
      this.confirm.open = true;
    },
    closeConfirm() {
      this.confirm.open = false;
    },
    confirmRemoveAll() {
      this.selectedIds = {};
      this.selectedList = [];
      this.closeConfirm();
    },
    getSelectedPackages() {
      return [...this.selectedList];
    },
    setSelectedPackages(packages) {
      const ids = {};
      packages.forEach(pkg => { ids[pkg.id] = true; });
      this.selectedIds = ids;
      this.selectedList = [...packages];
    },
    matchAndSetPackages(initialPkgs) {
      // Match initialPackages with allPackages by id to get full package data
      const matchedPackages = [];
      const ids = {};

      initialPkgs.forEach(initPkg => {
        // Compare with Number() to handle type mismatch
        const initId = Number(initPkg.id);
        const fullPkg = this.allPackages.find(pkg => Number(pkg.id) === initId);
        if (fullPkg) {
          matchedPackages.push(fullPkg);
          ids[fullPkg.id] = true;
        }
      });

      this.selectedIds = ids;
      this.selectedList = matchedPackages;
    },
    showTooltip(event, text) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltipText = text;
      this.tooltipX = rect.left + rect.width / 2;
      this.tooltipY = rect.top;
    },
    hideTooltip() {
      this.tooltipText = "";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.package-selector-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: var(--size-sm);
  margin-top: 24px;
  overflow-x: auto;
}

.package-selector-header {
  background-color: #f9fafb;
  padding: var(--size-md);
  border-bottom: 1px solid #e5e7eb;
  min-width: 700px;
}

.title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #374151;

  .required {
    color: #f56c6c;
    margin-left: 2px;
  }
}

.package-selector {
  display: grid;
  grid-template-columns: 1fr 2fr;
  height: 450px;
  max-height: 450px;
  min-width: 700px;
}

.search-list {
  position: relative;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .search-input-wrapper {
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
    height: 60px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .list-action-bar {
    display: flex;
    justify-content: flex-end;
    padding: 4px 12px;
    border-bottom: 1px solid #f0f0f0;
  }

  .select-all-button {
    background: none;
    border: none;
    padding: 2px 6px;
    color: #f5a623;
    cursor: pointer;
    font-size: 12px;
    white-space: nowrap;

    &:hover:not(:disabled) {
      color: #e08e00;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .list-body {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
  }
}

.selected-list {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
    height: 60px;

    .count {
      margin: 0;
      color: $primary-color;
      font-size: 16px;
      font-weight: bold;
    }

    .remove-all-button {
      background: none;
      border: none;
      padding: 2px 6px;
      color: #e63946;
      cursor: pointer;
      font-size: 12px;

      &:hover:not(:disabled) {
        color: #c5303c;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .cards-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-content: flex-start;
    min-height: 0;

    .empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #999;
      width: 100%;
      height: 100%;
    }
  }
}

.package-card {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  max-width: calc(50% - 6px);
  min-width: 180px;
  flex: 1 1 calc(50% - 6px);

  .card-icon {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .card-meta {
    flex: 1;
    padding: 0 10px;
    min-width: 0;

    .card-name {
      margin: 0;
      font-size: 13px;
      font-weight: 500;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .card-provider {
      margin: 2px 0 0;
      font-size: 11px;
      color: #999;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .card-remove-button {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    opacity: 0.6;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }

    img {
      width: 14px;
      height: 14px;
    }
  }
}

.package-option {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;

  &.selectable {
    cursor: pointer;

    &:hover {
      background: #f9f9f9;
    }
  }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .meta {
    flex: 1;
    padding-left: 12px;
    min-width: 0;

    .name {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .provider {
      margin: 4px 0 0;
      font-size: 12px;
      color: #999;
    }
  }
}

/* Responsive Design */
@media screen and (max-width: 768px) {
  .package-selector-header {
    padding: var(--size-md);
  }

  .title {
    font-size: 0.875rem;
  }
}

@media screen and (max-width: 480px) {
  .package-selector-header {
    padding: var(--size-sm);
  }

  .title {
    font-size: 0.75rem;
  }
}
</style>
